import { useCallback } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { useDispatch, useStore } from 'react-redux';
import { RootState, useAppSelector } from 'src/store';
import { useCampaignsApi } from 'src/modules/campaigns/api';
import { useCampaignBuilderV2Api } from 'src/modules/campaign-builder-v2/api';
import { contactCaptureApi } from '../api';
import { getErrorMessage, isEligibleContactCaptureCampaign } from '../helpers';
import {
  clearRecord,
  startRecordRequest,
  completeRecordRequest,
  failRecordRequest,
  completeListRequest,
  failListRequest,
  resetList,
  setFilter,
  setLoading,
  setCampaignOptions,
  rememberCampaign,
  setSelected,
  startListRequest,
  updateListRecord,
} from '../slices';
import { ContactCapture, ContactCaptureCampaign, ContactCaptureStatus, ContactCaptureUpsertData } from '../types';

const CAMPAIGN_PAGE_SIZE = 200;
const CAMPAIGN_CACHE_TTL_MS = 5 * 60 * 1000;

/** Concurrent list loads share one options request instead of each paging every campaign. */
const inFlightCampaignOptions = new Map<string, Promise<ContactCaptureCampaign[]>>();

const toContactCaptureCampaign = ({ id, name, type, status }: ContactCaptureCampaign): ContactCaptureCampaign =>
  ({ id, name, type, status });

export const useContactCaptureService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const state = useAppSelector(currentState => currentState.contactCapture);
  const campaignsApi = useCampaignsApi();
  const campaignApi = useCampaignBuilderV2Api();

  const loadCampaignOptions = useCallback(async (companyId: string, force = false) => {
    const cached = getState().contactCapture.campaignOptions;
    const isFresh = cached?.companyId === companyId
      && Date.now() - cached.fetchedAt < CAMPAIGN_CACHE_TTL_MS;
    if (isFresh && !force) return cached.items;
    const pending = inFlightCampaignOptions.get(companyId);
    if (pending) return pending;

    const request = (async () => {
      const items: ContactCaptureCampaign[] = [];
      let offset = 0;
      let count: number;
      do {
        const response = await campaignsApi.getCampaigns({
          companyId, limit: CAMPAIGN_PAGE_SIZE, offset, status: 'active', type: 'join',
          sort: 'name_asc',
        });
        // Check eligibility locally as well as requesting the server filter.
        items.push(...response.data.items.filter(isEligibleContactCaptureCampaign).map(toContactCaptureCampaign));
        offset += response.data.items.length;
        count = response.data.count;
        if (!response.data.items.length) break;
      } while (offset < count);
      dispatch(setCampaignOptions({ companyId, items }));
      return items;
    })();
    inFlightCampaignOptions.set(companyId, request);
    try {
      return await request;
    } finally {
      inFlightCampaignOptions.delete(companyId);
    }
  }, [dispatch, getState, campaignsApi]);

  // Inactive or deleted relationships still get a label in the list, but never
  // become selectable options in the builder.
  const hydrateCampaigns = useCallback(async (companyId: string, records: ContactCapture[]) => {
    const campaignIds = Array.from(new Set(
      records.map(record => record.campaignId).filter((id): id is string => Boolean(id)),
    ));
    if (!campaignIds.length) return records.map(record => ({ ...record, campaign: null }));
    const campaignsById = new Map<string, ContactCaptureCampaign>();
    try {
      (await loadCampaignOptions(companyId)).forEach(campaign => campaignsById.set(campaign.id, campaign));
    } catch {
      // Resolve the specific relationships if the list request failed.
    }
    const lookups = getState().contactCapture.campaignOptions;
    if (lookups?.companyId === companyId) {
      Object.values(lookups.lookups).forEach(campaign => {
        if (!campaignsById.has(campaign.id)) campaignsById.set(campaign.id, campaign);
      });
    }
    await Promise.all(campaignIds.filter(id => !campaignsById.has(id)).map(async id => {
      try {
        // A deleted campaign is expected here; the row shows it as unavailable instead of a toast.
        const { data } = await campaignApi.getCampaign(id, companyId, { skipGenericToast: true });
        const campaign = toContactCaptureCampaign(data);
        campaignsById.set(id, campaign);
        dispatch(rememberCampaign({ companyId, campaign, selectable: false }));
      } catch {
        // Not cached: a passing failure must not label the row until the options expire.
        campaignsById.set(id, { id, name: 'Unavailable campaign' });
      }
    }));
    return records.map(record => ({
      ...record,
      campaign: record.campaignId ? campaignsById.get(record.campaignId) || null : null,
    }));
  }, [campaignApi, dispatch, getState, loadCampaignOptions]);

  const addCampaignOption = useCallback((companyId: string, campaign: ContactCaptureCampaign) => {
    dispatch(rememberCampaign({ companyId, campaign, selectable: isEligibleContactCaptureCampaign(campaign) }));
  }, [dispatch]);

  const hydrateCampaign = useCallback(async (companyId: string, record: ContactCapture) => {
    const [hydrated] = await hydrateCampaigns(companyId, [record]);
    return hydrated;
  }, [hydrateCampaigns]);

  const getList = useCallback(async (companyId: string, filterOverride?: Partial<RootState['contactCapture']['filter']>) => {
    const filter = { ...getState().contactCapture.filter, ...filterOverride };
    const requestId = nanoid();
    dispatch(startListRequest({ companyId, requestId }));
    try {
      const response = await contactCaptureApi.list({
        companyId,
        offset: filter.offset,
        limit: filter.limit,
        query: filter.query || undefined,
        type: filter.type,
        status: filter.status,
        sort: filter.sort,
      });
      const items = await hydrateCampaigns(companyId, response.data.items);
      const result = { ...response.data, items };
      dispatch(completeListRequest({ companyId, requestId, ...result }));
      return result;
    } catch (error) {
      dispatch(failListRequest({ companyId, requestId, message: getErrorMessage(error, 'Could not load contact captures.') }));
      throw error;
    }
  }, [dispatch, getState, hydrateCampaigns]);

  /** Re-reads a record, then refreshes both the selected record and its list row. */
  const syncRecord = useCallback(async (companyId: string, id: string, overrides?: Partial<ContactCapture>) => {
    const response = await contactCaptureApi.read(companyId, id);
    const record = await hydrateCampaign(companyId, { ...response.data, ...overrides });
    dispatch(setSelected(record));
    dispatch(updateListRecord(record));
    return record;
  }, [dispatch, hydrateCampaign]);

  const read = useCallback(async (companyId: string, id: string) => {
    const requestId = nanoid();
    dispatch(startRecordRequest({ companyId, id, requestId }));
    try {
      const response = await contactCaptureApi.read(companyId, id);
      const record = await hydrateCampaign(companyId, response.data);
      dispatch(completeRecordRequest({ requestId, record }));
      return record;
    } catch (error) {
      dispatch(failRecordRequest({ requestId, message: getErrorMessage(error, 'Could not load this contact capture.') }));
      throw error;
    }
  }, [dispatch, hydrateCampaign]);

  const clearSelected = useCallback(() => { dispatch(clearRecord()); }, [dispatch]);

  /** A draft has never been published, so activating it has to publish instead. */
  const findCachedRecord = useCallback((companyId: string, id: string) => {
    const state = getState().contactCapture;
    const cached = state.list?.items.find(item => item.id === id) || state.selected;
    return cached?.id === id && cached.companyId === companyId ? cached : null;
  }, [getState]);

  const mutateStatus = useCallback(async (companyId: string, id: string, status: ContactCaptureStatus) => {
    dispatch(setLoading({ mutation: true }));
    try {
      const cached = findCachedRecord(companyId, id);
      const currentStatus = cached?.status
        ?? (await contactCaptureApi.read(companyId, id)).data.status;

      if (status === 'inactive') {
        await contactCaptureApi.deactivate(companyId, id);
      } else if (currentStatus === 'draft') {
        await contactCaptureApi.publish(companyId, id);
      } else {
        await contactCaptureApi.activate(companyId, id);
      }

      // Publishing a draft makes the live widget this version even if the read lags behind.
      return await syncRecord(
        companyId, id, status === 'active' && currentStatus === 'draft' ? { hasUnpublishedChanges: false } : undefined,
      );
    } finally {
      dispatch(setLoading({ mutation: false }));
    }
  }, [dispatch, findCachedRecord, syncRecord]);

  /**
   * Add and update answer with the summary view, which carries no consentText. It is the same
   * backend-owned text for every capture, so the one already loaded for this record is carried
   * across the mutation instead of costing a second request; a brand new record picks it up from
   * the details read its own route triggers.
   */
  const withKnownConsentText = useCallback((record: ContactCapture) => {
    if (record.consentText) return record;
    const known = findCachedRecord(record.companyId, record.id)?.consentText;
    return known ? { ...record, consentText: known } : record;
  }, [findCachedRecord]);

  const create = useCallback(async (data: ContactCaptureUpsertData) => {
    const response = await contactCaptureApi.create(data);
    const record = await hydrateCampaign(data.companyId, withKnownConsentText(response.data));
    dispatch(setSelected(record));
    return record;
  }, [dispatch, hydrateCampaign, withKnownConsentText]);

  const update = useCallback(
    async (companyId: string, id: string, data: ContactCaptureUpsertData) => {
      const response = await contactCaptureApi.update(companyId, id, data);
      const record = await hydrateCampaign(companyId, withKnownConsentText(response.data));
      dispatch(setSelected(record));
      dispatch(updateListRecord(record));
      return record;
    },
    [dispatch, hydrateCampaign, withKnownConsentText],
  );

  const duplicate = useCallback(async (companyId: string, id: string) => {
    const source = (await contactCaptureApi.read(companyId, id)).data;
    if (!source.allowedOrigins?.length) {
      throw new Error('Set the website domain before duplicating this contact capture.');
    }
    const campaign = source.campaignId
      ? await campaignApi.getCampaign(source.campaignId, companyId, { skipGenericToast: true })
        .then(response => response.data, () => null)
      : null;
    if (!isEligibleContactCaptureCampaign(campaign)) {
      throw new Error('Select an active join campaign before duplicating this contact capture.');
    }
    return contactCaptureApi.duplicate(companyId, source, campaign.id, source.allowedOrigins);
  }, [campaignApi]);

  const remove = useCallback(
    (companyId: string, id: string) => contactCaptureApi.delete(companyId, id),
    [],
  );

  const changeFilter = useCallback(
    (filter: Partial<RootState['contactCapture']['filter']>) => dispatch(setFilter(filter)),
    [dispatch],
  );

  const clearList = useCallback(() => dispatch(resetList()), [dispatch]);

  const publish = useCallback(async (companyId: string, id: string) => {
    dispatch(setLoading({ mutation: true }));
    try {
      await contactCaptureApi.publish(companyId, id);
      // The publish succeeded, so the live widget is this version even if the read lags behind.
      return await syncRecord(companyId, id, { hasUnpublishedChanges: false });
    } finally {
      dispatch(setLoading({ mutation: false }));
    }
  }, [dispatch, syncRecord]);

  return {
    ...state,
    getList,
    loadCampaignOptions,
    addCampaignOption,
    read,
    clearSelected,
    create,
    update,
    publish,
    mutateStatus,
    duplicate,
    remove,
    changeFilter,
    clearList,
  };
};
